/**
 * How a live media placeholder becomes a picture: the WS-relay completion swap
 * and the POLL fallback behind it. Split out of `chat.ts` (500-LOC ceiling) with
 * every dependency injected (DIP) — it reads the stream it resolves into and the
 * companion the view is on through accessors, so it can be driven with no store.
 *
 * Both paths RE-VALIDATE the companion after their awaits: a switch mid-fetch
 * already reset the stream, so committing the fetch would splice one companion's
 * assets into another's thread.
 */
import { isErr } from '@4f/shared';
import type { ApiClient } from '../api/client.ts';
import {
  pendingMediaJobIds,
  resolveMediaAssets,
  type StreamState,
} from '../features/chat/stream-model.ts';
import type { WsMediaJobUpdate } from '../realtime/ws.ts';
import type { ChatScheduler } from './chat-delivery.ts';

/** How often a still-pending media placeholder is reconciled against the edge. */
export const MEDIA_POLL_INTERVAL_MS = 6_000;

/** Everything the reconciler needs from the store that owns the stream. */
export interface MediaReconcilerDeps {
  readonly api: Pick<ApiClient, 'getMediaJob' | 'listGallery'>;
  readonly schedule: ChatScheduler;
  /** The live run's view — the source of the pending placeholders. */
  readonly stream: () => StreamState;
  /** Fold a resolution into that view. */
  readonly setStream: (update: (current: StreamState) => StreamState) => void;
  /** The companion the view is on RIGHT NOW (re-read after every await). */
  readonly companionId: () => string | null;
  /** True when her media is pre-generated, so reveals run the faked beat. */
  readonly prebuilt: () => boolean;
}

export interface MediaReconciler {
  /**
   * Reconcile every still-pending placeholder against the edge's job status —
   * the POLL fallback behind the WS push, so a tab that lost its socket still
   * resolves the shimmer instead of spinning forever.
   */
  reconcile(): Promise<void>;
  /**
   * The WS-relay completion swap: on a `done` push for the OPEN companion's job,
   * re-fetch her gallery and resolve the matching placeholder's `src`. A no-op
   * for another companion's job, a non-`done` status, or a gallery read failure
   * (the placeholder just stays pending for the next push/poll).
   */
  resolveJob(update: WsMediaJobUpdate): Promise<void>;
  /** Arm the self-rescheduling poll if anything is still pending. */
  schedulePoll(): void;
  /** Cancel the armed poll (companion switch, dispose). */
  stopPolling(): void;
}

export function createMediaReconciler(deps: MediaReconcilerDeps): MediaReconciler {
  let cancelPoll: (() => void) | null = null;

  /** Re-read her gallery and resolve what it can — shared by both paths. */
  const applyGallery = async (gfId: string): Promise<void> => {
    const gallery = await deps.api.listGallery(gfId);
    if (isErr(gallery) || deps.companionId() !== gfId) {
      return;
    }
    const faked = deps.prebuilt();
    deps.setStream((current) => resolveMediaAssets(current, gallery.value, { faked }));
  };

  const resolveJob = async (update: WsMediaJobUpdate): Promise<void> => {
    if (update.status !== 'done' || deps.companionId() !== update.girlfriendId) {
      return;
    }
    await applyGallery(update.girlfriendId);
  };

  /**
   * Reads each pending placeholder's job status; the first one that reports
   * `done` triggers ONE gallery re-read that resolves every placeholder it can.
   */
  const reconcile = async (): Promise<void> => {
    const gfId = deps.companionId();
    const jobIds = pendingMediaJobIds(deps.stream());
    if (!gfId || jobIds.length === 0) {
      return;
    }
    const statuses = await Promise.all(jobIds.map((jobId) => deps.api.getMediaJob(jobId)));
    const settled = statuses.some((result) => !isErr(result) && result.value.status === 'done');
    if (!settled) {
      return;
    }
    await applyGallery(gfId);
  };

  /**
   * Keep reconciling while any placeholder is still pending, then stop. Self-
   * rescheduling rather than a fixed interval so the loop cannot outlive the
   * work: no pending media ⇒ no timer ⇒ no background traffic.
   */
  const schedulePoll = (): void => {
    if (cancelPoll || pendingMediaJobIds(deps.stream()).length === 0) {
      return;
    }
    cancelPoll = deps.schedule(() => {
      cancelPoll = null;
      void reconcile().finally(() => schedulePoll());
    }, MEDIA_POLL_INTERVAL_MS);
  };

  return {
    reconcile,
    resolveJob,
    schedulePoll,
    stopPolling(): void {
      cancelPoll?.();
      cancelPoll = null;
    },
  };
}
